// The v2 base components (docs/design/brand-direction/APP-SHELL-v2.md). One white primary per screen, everything
// else outline or text; colour only for departments; people neutral; at most one loud moment per screen.
// Styles: ui/kit.css.
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  type ReactNode,
  type Ref,
  useEffect,
  useId,
  useRef,
} from "react";
import { creditsText, eurOfCredits, eurText, leftCredits, moneyText } from "../data/money.ts";
import type { SpendDept } from "../data/sources.ts";
import { spendColor, spendSegments } from "../data/spendBar.ts";
import type { DeptStep } from "../data/steps.ts";
import { DEPARTMENT_LABEL, type Department, deptColor, isExtra } from "../departments.ts";
import { Avatar } from "./collab/Avatars.tsx";
import { containDialogFocus } from "./dialogFocus.ts";
import { useHoverClip } from "./HoverClip.tsx";
import { Icon, type IconName } from "./icons.tsx";
import { Picture } from "./Picture.tsx";
import { familySide, SquareStrip, StatusSquare } from "./StatusSquare.tsx";

// ── buttons and chips ─────────────────────────────────────────────────────────────────────────────────────

export type ButtonVariant = "primary" | "outline" | "text";

/**
 * A button. `primary` is the screen's one white action; `outline` (the default) and `text` are everything else.
 * `price` shows what an expensive action costs, in credits and euros, on the button itself.
 */
export function Button({
  variant = "outline",
  size = "md",
  icon,
  price,
  className = "",
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  ref?: Ref<HTMLButtonElement>;
  icon?: IconName;
  /** credits */
  price?: number | null;
}) {
  return (
    <button type="button" className={`k-btn k-${variant} k-${size} ${className}`} {...rest}>
      {icon && <Icon name={icon} size={size === "lg" ? 16 : 14} />}
      {children}
      {/* spelled out, as everywhere money shows: "1 credit · €0.05" */}
      {price != null && <span className="k-price">· {moneyText(price)}</span>}
    </button>
  );
}

/** A link styled as a button (navigation, not an action). */
export function LinkButton({
  href,
  variant = "outline",
  size = "md",
  icon,
  children,
  className = "",
  external = false,
}: {
  href: string;
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  icon?: IconName;
  children: ReactNode;
  className?: string;
  /** leaves the studio (the docs): a new tab, with the small arrow that says so */
  external?: boolean;
}) {
  return (
    <a
      href={href}
      className={`k-btn k-${variant} k-${size} ${className}`}
      {...(external ? { target: "_blank", rel: "noopener" } : {})}
    >
      {icon && <Icon name={icon} size={14} />}
      {children}
      {external && (
        <>
          <Icon name="external" size={12} className="k-ext" />
          <span className="sr-only"> (opens in a new tab)</span>
        </>
      )}
    </a>
  );
}

/** A setting chip ("Camera Auto ⌄"), an output chip ("21:9") or a filter chip. Pressed = chosen. */
export function Chip({
  label,
  value,
  dept,
  pressed,
  caret,
  count,
  onClick,
  title,
}: {
  label?: string;
  value?: ReactNode;
  dept?: Department | null;
  pressed?: boolean;
  caret?: boolean;
  count?: number;
  onClick?: () => void;
  title?: string;
}) {
  return (
    <button type="button" className="k-chip" aria-pressed={pressed} onClick={onClick} title={title}>
      {dept && <DeptDot dept={dept} />}
      {label && <span className="k-chip-label">{label}</span>}
      {value != null && <span className="k-chip-value">{value}</span>}
      {count != null && <span className="k-chip-count">{count}</span>}
      {caret && <Icon name="chevron" size={10} className="down" />}
    </button>
  );
}

// ── departments ───────────────────────────────────────────────────────────────────────────────────────────

/** The small square in a department's colour. Decoration next to a word that names the department. */
export function DeptDot({ dept, size = 8 }: { dept: Department; size?: number }) {
  return (
    <span
      className="k-dot"
      style={{ background: deptColor(dept), width: size, height: size }}
      aria-hidden="true"
    />
  );
}

/** "■ Sets": a department named with its colour. */
export function DeptName({ dept, children }: { dept: Department; children?: ReactNode }) {
  return (
    <span className="k-deptname">
      <DeptDot dept={dept} />
      {children ?? DEPARTMENT_LABEL[dept]}
    </span>
  );
}

/** The squares of a production: the nine, and the extras it runs (narrower, next to their parent). */
export function Strip({
  steps,
  size = 6,
  label,
}: {
  steps: readonly DeptStep[];
  size?: number;
  label?: string;
}) {
  return <SquareStrip steps={steps} size={size} label={label} />;
}

/** A square and a plain status line ("Waiting for you"). */
export function StatusLabel({
  step,
  children,
}: {
  step: Pick<DeptStep, "dept" | "state">;
  children: ReactNode;
}) {
  return (
    <span className={`k-status k-status-${step.state}`}>
      <StatusSquare state={step.state} dept={step.dept} size={12} />
      <span>{children}</span>
    </span>
  );
}

/**
 * The column widths of the step strip: a core department one share, an extra a narrower one (`wide`), or fixed
 * widths for the strip that scrolls on a narrow screen.
 */
export function stepColumns(steps: readonly { dept: Department }[], scroll = false): string {
  return steps
    .map((s) =>
      isExtra(s.dept) ? (scroll ? "92px" : "minmax(0, 0.62fr)") : scroll ? "128px" : "minmax(0, 1fr)",
    )
    .join(" ");
}

/**
 * The department strip that doubles as navigation on the board: one cell per step the production has (number,
 * name, a plain line), the selected one raised. A core department carries its number (01 to 09); an extra one is
 * a narrower cell without a number, right against the department it belongs to. Arrow keys move between cells (a
 * single tab stop, like a tab list).
 */
export function StepNav({
  steps,
  current,
  hrefOf,
  label = "Departments",
}: {
  steps: readonly DeptStep[];
  current: Department;
  hrefOf: (d: Department) => string;
  label?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  // A scrolling strip ends on a cell start too: a little room after the last cell, so the far end never shows a
  // cell cut in half on the left.
  useEffect(() => {
    const nav = ref.current;
    if (!nav || typeof ResizeObserver === "undefined") return;
    const fit = () => {
      nav.style.paddingRight = "";
      const max = nav.scrollWidth - nav.clientWidth;
      if (max <= 0) return;
      const starts = [...nav.children].map((c) => (c as HTMLElement).offsetLeft);
      const end = starts.find((x) => x >= max - 1);
      if (end !== undefined && end - max > 1) nav.style.paddingRight = `${end - max}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(nav);
    return () => ro.disconnect();
  }, []);
  // On a narrow screen the strip scrolls: keep the department in view visible.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs again when the department in view changes
  useEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>("[aria-current]");
    const nav = ref.current;
    // the cell before it stays in view, whole: the strip starts on a cell (scroll-snap does the rest)
    if (el && nav && nav.scrollWidth > nav.clientWidth) {
      const prev = el.previousElementSibling as HTMLElement | null;
      nav.scrollLeft = (prev ?? el).offsetLeft; // the strip is position: relative, the offset parent
    }
  }, [current]);
  return (
    <nav
      className="k-stepnav"
      aria-label={label}
      ref={ref}
      style={{ "--cols": stepColumns(steps), "--cols-scroll": stepColumns(steps, true) } as CSSProperties}
      onKeyDown={(e) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        const links = [...(ref.current?.querySelectorAll<HTMLAnchorElement>("a") ?? [])];
        const i = links.indexOf(document.activeElement as HTMLAnchorElement);
        if (i < 0) return;
        e.preventDefault();
        links[(i + (e.key === "ArrowRight" ? 1 : links.length - 1)) % links.length]?.focus();
      }}
    >
      {steps.map((s, i) => {
        const on = s.dept === current;
        const extra = isExtra(s.dept);
        const side = familySide(steps, i);
        const cls = `${extra ? " k-step-extra" : ""}${side ? ` k-step-${side}` : ""}`;
        const n = steps.slice(0, i + 1).filter((x) => !isExtra(x.dept)).length;
        const inner = (
          <>
            <span className="k-step-top">
              <StatusSquare state={s.state} dept={s.dept} size={9} narrow={extra} />
              {!extra && <span className="k-step-n">{String(n).padStart(2, "0")}</span>}
            </span>
            <span className="k-step-name">{DEPARTMENT_LABEL[s.dept]}</span>
            <span className="k-step-label">{s.label}</span>
          </>
        );
        // a step the production skips is not a place to go
        if (s.state === "skipped" && !on)
          return (
            <span
              key={s.dept}
              className={`k-step k-step-skipped${cls}`}
              style={{ "--dept": deptColor(s.dept) } as CSSProperties}
              title={`${DEPARTMENT_LABEL[s.dept]}: skipped in this production`}
            >
              {inner}
            </span>
          );
        return (
          <a
            key={s.dept}
            href={hrefOf(s.dept)}
            className={`k-step k-step-${s.state}${cls}`}
            aria-current={on ? "page" : undefined}
            style={{ "--dept": deptColor(s.dept) } as CSSProperties}
            title={`${DEPARTMENT_LABEL[s.dept]}: ${s.hint ?? s.label}`}
          >
            {inner}
          </a>
        );
      })}
    </nav>
  );
}

// ── stills and cards ──────────────────────────────────────────────────────────────────────────────────────

const isUrl = (s: string) => /^(https?:|data:|blob:|\/)/.test(s) || s.includes(".");

/**
 * One still: a URL (demo stills) or an asset version id / `element:<id>:<v>` (real work, through Picture).
 */
export function Still({
  src,
  alt,
  className = "",
  decorative = false,
  full = false,
}: {
  src: string | null | undefined;
  alt: string;
  className?: string;
  decorative?: boolean;
  full?: boolean;
}) {
  if (src && isUrl(src))
    return (
      <div className={`pic k-still ${className}`} aria-hidden={decorative || undefined}>
        <img src={src} alt={decorative ? "" : alt} loading="lazy" draggable={false} />
      </div>
    );
  return (
    <Picture
      thumb={src ?? null}
      alt={alt}
      glyph="shot"
      className={`k-still ${className}`}
      decorative={decorative}
      full={full}
    />
  );
}

export interface CardPerson {
  id: string;
  name: string;
}

/**
 * A production card (APP-SHELL-v2): big cover still, title, one meta line, the strip of its steps (the nine, plus
 * the extras it runs) with a plain line under it, crew avatars and spend.
 */
export function ProductionCard({
  href,
  title,
  meta,
  cover,
  coverAlt,
  steps,
  line,
  people = [],
  spentCredits,
  clip = null,
  actions,
}: {
  href: string;
  title: string;
  meta: string;
  cover: string | null;
  coverAlt: string;
  /** a video of the production, played muted on hover */
  clip?: string | null;
  steps: readonly DeptStep[] | null;
  line: string | null;
  people?: readonly CardPerson[];
  spentCredits: number | null;
  /** a ⋯ menu on the card's corner, beside the link (never inside it) */
  actions?: ReactNode;
}) {
  const hover = useHoverClip(clip);
  const card = (
    <a className="k-card k-prodcard" href={href} {...hover.handlers}>
      <span className="hc-frame">
        <Still src={cover} alt={coverAlt} className="k-prodcard-cover" />
        {hover.layer}
      </span>
      <span className="k-prodcard-body">
        <span className="k-prodcard-title">{title}</span>
        <span className="k-meta">{meta}</span>
        {steps ? (
          <>
            <Strip steps={steps} size={11} label={`${title}: production steps`} />
            <span className="k-prodcard-line">{line}</span>
          </>
        ) : (
          <span className="k-prodcard-line k-loading-line" aria-hidden="true" />
        )}
        <span className="k-prodcard-foot">
          <span className="k-faces">
            {people.slice(0, 4).map((p) => (
              <Avatar key={p.id} userId={p.id} name={p.name} size={20} />
            ))}
          </span>
          {spentCredits != null && <span className="k-money">{moneyText(spentCredits)}</span>}
        </span>
      </span>
    </a>
  );
  if (!actions) return card;
  return (
    <div className="k-prodcard-wrap">
      {card}
      <div className="k-prodcard-actions">{actions}</div>
    </div>
  );
}

// ── signature lines and loud moments ──────────────────────────────────────────────────────────────────────

/** The nine-segment spectrum line: the signature divider. */
export function Spectrum({ height = 2 }: { height?: number }) {
  return <div className="k-spectrum" style={{ height }} aria-hidden="true" />;
}

/**
 * A loud moment (drops, offers, live events): a white card with the spectrum line on its top edge, ink text.
 * Never in a department colour, at most one per screen, never inside a work step.
 */
export function LoudCard({
  eyebrow,
  title,
  children,
  action,
  onAction,
  aside,
}: {
  eyebrow: ReactNode;
  title: string;
  children?: ReactNode;
  action?: string;
  onAction?: () => void;
  aside?: ReactNode;
}) {
  const id = useId();
  return (
    <section className="k-loud" aria-labelledby={id}>
      <Spectrum height={4} />
      <div className="k-loud-body">
        <div className="k-loud-text">
          <p className="k-loud-eyebrow">{eyebrow}</p>
          <h2 id={id}>{title}</h2>
          {children && <p className="k-loud-copy">{children}</p>}
          {action && (
            <button type="button" className="k-loud-action" onClick={onAction}>
              {action}
              <Icon name="arrow" size={13} />
            </button>
          )}
        </div>
        {aside}
      </div>
    </section>
  );
}

// ── money ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The credits meter of the sidebar: a bar that measures this month's use against what there is to use, split by
 * department in their colours, then the empty rest. Metered: against the use plus the balance left. Not metered:
 * against the plan's monthly credits when they are known, otherwise no bar at all (a bar split by the use alone always
 * reads full, and a full bar says "budget gone"). Under it the balance (or the use) in credits and euros.
 */
export function CreditsMeter({
  metered,
  credits,
  usedCredits,
  usedByDept,
  addHref,
  addLabel,
  planCredits,
  monthlyCredits,
}: {
  metered: boolean;
  /** the balance; ignored when not metered */
  credits: number;
  usedCredits: number;
  usedByDept: Partial<Record<SpendDept, number>>;
  addHref: string;
  /** the link under the meter; "Add credits" by default ("Get seats" on Free, where packs come with a plan) */
  addLabel?: string;
  /** the part of the balance that comes from the plan, spent first; shown when there is some */
  planCredits?: number;
  /** the plan's credits per month; the bar of a studio that is not metered measures against them */
  monthlyCredits?: number;
}) {
  const allowance = !metered && monthlyCredits != null && monthlyCredits >= 1 ? monthlyCredits : null;
  const scaled = metered || allowance != null;
  const segments = metered
    ? spendSegments(usedByDept, credits)
    : allowance != null
      ? spendSegments(usedByDept, Math.max(allowance - usedCredits, 0))
      : [];
  const line = meterLine({ metered, credits, usedCredits, monthlyCredits });
  const label = segments.length
    ? `${line.main}${line.of ? ` ${line.of}` : ""}. Used this month, by department: ${segments.map((s) => `${s.label} ${creditsText(s.credits)}`).join(", ")}`
    : `${line.main}${line.of ? ` ${line.of}` : ""}. Nothing used yet this month`;
  const plan = metered && planCredits != null && planCredits >= 1 ? leftCredits(planCredits) : null;
  return (
    <div className="k-meter">
      {scaled && (
        <div className="k-meter-bar" role="img" aria-label={label}>
          {segments.map((s) => (
            <span key={s.dept} style={{ width: `${s.share * 100}%`, background: spendColor(s.dept) }} />
          ))}
        </div>
      )}
      {/* one mono line: what there is, and of what (both in credits and euros) */}
      <p className="k-money k-meter-money">
        <span>{line.main}</span> {line.of && <span className="k-meter-of">{line.of}</span>}
      </p>
      <p className="k-meter-foot">
        <span>{line.label}</span>
        <a
          className="link"
          href={addHref}
          // the plan and pack split is detail: a tooltip here, the whole story on the Credits page
          title={plan != null ? `${moneyText(plan, "long")} of it from your plan, spent first.` : undefined}
        >
          {metered ? (addLabel ?? "Add credits") : "Usage"}
        </a>
      </p>
    </div>
  );
}

/**
 * The meter in words: the main figure, what it is out of, and a plain label. Metered: "1,240 credits · €62.00"
 * "left of 1,466 · €73.30" (use plus what is left). Not metered: "160 credits · €8.00" "used of 1,600 · €80.00"
 * when the plan's monthly credits are known, else the use alone. Pure.
 */
export function meterLine({
  metered,
  credits,
  usedCredits,
  monthlyCredits,
}: {
  metered: boolean;
  credits: number;
  usedCredits: number;
  monthlyCredits?: number | null;
}): { main: string; of: string | null; label: string } {
  const short = (c: number) => `${creditsText(c)} · ${eurText(eurOfCredits(c))}`;
  if (metered) {
    const left = leftCredits(credits);
    return {
      main: moneyText(left, "long"),
      of: `left of ${short(leftCredits(usedCredits + Math.max(credits, 0)))}`,
      label: "This month",
    };
  }
  const allowance = monthlyCredits != null && monthlyCredits >= 1 ? leftCredits(monthlyCredits) : null;
  return {
    main: moneyText(usedCredits, "long"),
    of: allowance != null ? `used of ${short(allowance)}` : null,
    label: usedCredits > 0 ? (allowance != null ? "This month" : "Used this month") : "Nothing used yet",
  };
}

// ── dialog ────────────────────────────────────────────────────────────────────────────────────────────────

/** A modal dialog: focus stays inside, Escape closes, focus returns to what opened it. */
export function Dialog({
  title,
  eyebrow,
  lead,
  onClose,
  children,
  footer,
  wide = false,
  titleHidden = false,
  className = "",
  closable = true,
}: {
  title: string;
  eyebrow?: ReactNode;
  /** one quiet line under the title (a reassurance, what this is for) */
  lead?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** the title names the dialog for assistive technology only (a picture viewer) */
  titleHidden?: boolean;
  className?: string;
  /** false: a dialog that must be answered (no close button; Escape and a click outside do nothing) */
  closable?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    return containDialogFocus(el, el.querySelector<HTMLElement>("[data-autofocus]") ?? el);
  }, []);
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the scrim closes on a click outside; Escape does too
    <div
      className="k-scrim"
      onMouseDown={(e) => {
        if (closable && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        className={`k-dialog${wide ? " wide" : ""}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            if (closable) onClose();
          }
        }}
      >
        <header className="k-dialog-head">
          <div>
            {eyebrow && <p className="k-dialog-eyebrow">{eyebrow}</p>}
            <h2 id={id} className={titleHidden ? "sr-only" : undefined}>
              {title}
            </h2>
            {lead && <p className="k-dialog-lead">{lead}</p>}
          </div>
          {closable && (
            <button type="button" className="k-iconbtn" aria-label="Close" onClick={onClose}>
              <Icon name="close" size={14} />
            </button>
          )}
        </header>
        <div className="k-dialog-body">{children}</div>
        {footer && <footer className="k-dialog-foot">{footer}</footer>}
      </div>
    </div>
  );
}

// ── page furniture ────────────────────────────────────────────────────────────────────────────────────────

/** A page heading: big, tight, left-aligned, with an optional line under it and the screen's actions. */
export function PageHead({
  title,
  children,
  actions,
  big = true,
}: {
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  big?: boolean;
}) {
  return (
    <header className={`k-pagehead${big ? " big" : ""}`}>
      <div className="k-pagehead-text">
        <h1>{title}</h1>
        {children && <div className="k-pagehead-sub">{children}</div>}
      </div>
      {actions && <div className="k-pagehead-actions">{actions}</div>}
    </header>
  );
}

/** A quiet note for a screen or section that has nothing yet, with what to do about it. */
export function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="k-empty">
      <p className="k-empty-title">{title}</p>
      {children && <p className="k-empty-text">{children}</p>}
      {action}
    </div>
  );
}

/** A loading block that keeps the layout's shape (no spinner). */
export function Skeleton({ h = 120, className = "" }: { h?: number; className?: string }) {
  return <div className={`k-skel ${className}`} style={{ height: h }} aria-hidden="true" />;
}
